import { useUnit } from 'effector-react';
import {
  Building2,
  CloudUpload,
  Download,
  FileText,
  HardDrive,
  Landmark,
  Sunset,
  KeyRound,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $library,
  $syncMetas,
  $trash,
  importConfirmed,
  listProjectsFx,
  restoreRequested,
  trashRequested,
  type ProjectMeta,
} from '../../entities/library';
import { $user } from '../../entities/session';
import { AccountMenu } from '../../features/account';
import { exportRequested, importFileChosen } from '../../features/project-transfer';
import {
  buildLocalizedProject,
  CreateProjectDialog,
  projectCreated,
  PROJECT_TEMPLATES,
  type ProjectTemplate,
} from '../../features/create-project';
import { PreferencesMenu } from '../../features/preferences-menu';
import { HelpMenu } from '../../widgets/help-center';
import { formatRelativeTime } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { routes } from '../../shared/routing';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Wordmark,
} from '../../shared/ui';
import { $sampleWanted, projectDeleteConfirmed, sampleCreated } from './model';

const TEMPLATE_ICONS: Record<ProjectTemplate, ReactNode> = {
  blank: <FileText />,
  rentVsBuy: <Building2 />,
  earlyPayoff: <Landmark />,
  retireEarly: <Sunset />,
  buyToLet: <KeyRound />,
};

export function HomePage() {
  const { t } = useTranslation();
  const [library, loading] = useUnit([$library, listProjectsFx.pending]);
  const [creating, setCreating] = useState<ProjectTemplate | null>(null);
  const [sampleWanted, create, sampleDone] = useUnit([
    $sampleWanted,
    projectCreated,
    sampleCreated,
  ]);

  // First visit: open a sample project (it starts the tour).
  useEffect(() => {
    if (!sampleWanted) return;
    sampleDone();
    create(
      buildLocalizedProject(
        'rentVsBuy',
        t('home.sampleName', { name: t('createProject.templates.rentVsBuy.title') }),
        t,
      ),
    );
  }, [sampleWanted, sampleDone, create, t]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-11 shrink-0 items-center justify-between border-b border-border-subtle bg-bg-1 px-3">
        <div className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-5" />
          <Wordmark label={t('app.name')} className="text-text-1" />
        </div>
        <div className="flex items-center gap-1">
          <HelpMenu />
          <PreferencesMenu />
          <AccountMenu />
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-3xl px-4 py-8">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-lg font-semibold">{t('home.title')}</h1>
            <div className="flex items-center gap-2">
              <ImportJsonButton />
              <Button
                variant="primary"
                leadingIcon={<Plus />}
                onClick={() => {
                  setCreating('rentVsBuy');
                }}
              >
                {t('home.newProject')}
              </Button>
            </div>
          </div>

          {library.length > 0 ? (
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle bg-bg-1">
              {library.map((meta) => (
                <ProjectRow key={meta.id} meta={meta} />
              ))}
            </ul>
          ) : (
            !loading && (
              <section className="rounded-lg border border-border-subtle bg-bg-1 p-6">
                <h2 className="text-base font-medium">{t('home.emptyTitle')}</h2>
                <p className="mt-1 text-sm text-text-3">{t('home.emptyBody')}</p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-3">
                  {PROJECT_TEMPLATES.map((template) => (
                    <li key={template}>
                      <button
                        type="button"
                        onClick={() => {
                          setCreating(template);
                        }}
                        className="flex h-full w-full flex-col items-start gap-2 rounded-lg border border-border-subtle bg-bg-2 p-3 text-left outline-none hover:border-border-strong hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus"
                      >
                        <span className="text-text-3 [&_svg]:size-5">
                          {TEMPLATE_ICONS[template]}
                        </span>
                        <span className="text-sm font-medium">
                          {t(`createProject.templates.${template}.title`)}
                        </span>
                        <span className="text-xs text-text-3">
                          {t(`createProject.templates.${template}.body`)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )
          )}
          <TrashSection />
        </div>
      </main>

      {creating && (
        <CreateProjectDialog
          open
          initialTemplate={creating}
          onOpenChange={(open) => {
            if (!open) setCreating(null);
          }}
        />
      )}
    </div>
  );
}

/** Picks a `.json` export and adds it as a new project. */
function ImportJsonButton() {
  const { t } = useTranslation();
  const choose = useUnit(importFileChosen);
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="application/json,.json"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) choose(file);
          e.target.value = '';
        }}
      />
      <Button
        leadingIcon={<Upload />}
        onClick={() => {
          input.current?.click();
        }}
      >
        {t('home.importJson')}
      </Button>
    </>
  );
}

/** The account's trash (signed in only): restorable for 30 days. */
function TrashSection() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [user, trash, load, restore] = useUnit([$user, $trash, trashRequested, restoreRequested]);
  const [open, setOpen] = useState(false);
  const [now] = useState(() => Date.now());
  if (!user) return null;
  return (
    <section className="mt-6">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          if (!open) load();
          setOpen(!open);
        }}
        className="flex items-center gap-1.5 rounded-md px-1 py-0.5 text-sm text-text-2 outline-none hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus"
      >
        <Trash2 className="size-4" aria-hidden />
        {t('home.trash')}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-text-3">{t('home.trashHint')}</p>
          {trash.length === 0 ? (
            <p className="text-sm text-text-3">{t('home.trashEmpty')}</p>
          ) : (
            <ul className="divide-y divide-border-subtle rounded-lg border border-border-subtle bg-bg-1">
              {trash.map((m) => (
                <li key={m.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{m.name}</span>
                    <span className="block text-xs text-text-3">
                      {t('home.deleted', {
                        time: formatRelativeTime(m.deletedAt ?? now, now, locale),
                      })}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    leadingIcon={<RotateCcw />}
                    onClick={() => {
                      restore(m.id);
                    }}
                  >
                    {t('home.restore')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ProjectRow({ meta }: { meta: ProjectMeta }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [open, confirmDelete, exportJson, user, syncMetas, addToAccount] = useUnit([
    routes.project.open,
    projectDeleteConfirmed,
    exportRequested,
    $user,
    $syncMetas,
    importConfirmed,
  ]);
  const sync = syncMetas[meta.id];
  const synced = user !== null && sync?.ownerId === user.id;
  const [deleting, setDeleting] = useState(false);
  const [now] = useState(() => Date.now());

  return (
    <li className="group flex items-center gap-2 pr-2 hover:bg-bg-3">
      <button
        type="button"
        onClick={() => {
          void open({ projectId: meta.id });
        }}
        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left outline-none focus-visible:bg-bg-3"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{meta.name}</span>
            {user && !synced && (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-sm bg-bg-3 px-1.5 py-0.5 text-2xs text-text-2">
                <HardDrive className="size-3" aria-hidden />
                {t('home.localOnly')}
              </span>
            )}
          </span>
          <span className="block text-xs text-text-3">
            {t('home.variants', { count: meta.scenarioCount })} ·{' '}
            {t('home.edited', { time: formatRelativeTime(meta.updatedAt, now, locale) })}
          </span>
        </span>
      </button>
      <Menu>
        <MenuTrigger asChild>
          <IconButton
            label={t('home.actions')}
            className="opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 aria-expanded:opacity-100"
          >
            <MoreHorizontal />
          </IconButton>
        </MenuTrigger>
        <MenuContent>
          {user && !synced && (
            <MenuItem
              icon={<CloudUpload />}
              onSelect={() => {
                addToAccount([meta.id]);
              }}
            >
              {t('home.addToAccount')}
            </MenuItem>
          )}
          <MenuItem
            icon={<Download />}
            onSelect={() => {
              exportJson(meta.id);
            }}
          >
            {t('home.exportJson')}
          </MenuItem>
          <MenuItem
            icon={<Trash2 />}
            onSelect={() => {
              setDeleting(true);
            }}
          >
            {t('home.delete')}
          </MenuItem>
        </MenuContent>
      </Menu>
      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent
          title={t('home.deleteTitle', { name: meta.name })}
          description={synced ? t('home.deleteBodySynced') : t('home.deleteBody')}
          closeLabel={t('common.close')}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">{t('common.cancel')}</Button>
              </DialogClose>
              <Button
                variant="danger"
                onClick={() => {
                  confirmDelete(meta.id);
                  setDeleting(false);
                }}
              >
                {t('common.delete')}
              </Button>
            </>
          }
        />
      </Dialog>
    </li>
  );
}
